// =========================================
// BY ZY — Override Engine
// Loads admin.overrides.json and provides helpers
// for applying text, style, media, structure overrides.
// =========================================

import adminOverridesFile from '../data/admin.overrides.json';

// Module-level singleton — updated by App (public) or EditorApp (admin)
let _overrides = { version: 1, updatedAt: '', elements: {}, structure: {} };

// ---- Initialization ----

/** Load overrides from the bundled admin.overrides.json file. */
export function loadFileOverrides() {
  if (
    adminOverridesFile &&
    typeof adminOverridesFile.elements === 'object'
  ) {
    _overrides = adminOverridesFile;
  }
}

/** Set overrides directly (used by the admin editor for live preview). */
export function setOverrides(data) {
  if (data && typeof data.elements === 'object') {
    _overrides = data;
  }
}

/**
 * Get the general UI settings from overrides (e.g. cursorFluid color/opacity).
 * Returns the `ui` sub-object of the current overrides, or an empty object.
 */
export function getUiSettings() {
  return _overrides.ui || {};
}

/** Get the current overrides object. */
export function getOverrides() {
  return _overrides;
}

// ---- Clamping helpers ----

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const STYLE_RANGES = {
  fontSize: { min: 6, max: 80 },
  fontWeight: { min: 100, max: 900 },
  letterSpacing: { min: -0.15, max: 0.5 },
  lineHeight: { min: 0.8, max: 3.5 },
  opacity: { min: 0, max: 1 },
  borderRadius: { min: 0, max: 48 },
  glassBlur: { min: 0, max: 100 },
  glassAlpha: { min: 0, max: 1 },
  paddingX: { min: 0, max: 120 },
  paddingY: { min: 0, max: 120 },
  // These pass straight through to GlassCard as custom config
  blur: { min: 5, max: 50 },
  refraction: { min: 0.05, max: 0.40 },
  depth: { min: 0, max: 30 }
};

// ---- Public helpers ----

/**
 * Get text override for an element, or the fallback if no override.
 * @param {string} editId  — stable element ID
 * @param {string} fallback — original text
 */
export function getText(editId, fallback) {
  const entry = _overrides.elements?.[editId];
  if (entry && typeof entry.text === 'string') return entry.text;
  return fallback;
}

/**
 * Get inline style override for an element.
 * Returns an object suitable for React's `style` prop, or undefined.
 * All numeric values are clamped to safe ranges.
 */
export function getStyle(editId) {
  const entry = _overrides.elements?.[editId];
  if (!entry || !entry.style) return undefined;

  const s = entry.style;
  const out = {};

  if (typeof s.fontSize === 'number') {
    out.fontSize = clamp(s.fontSize, STYLE_RANGES.fontSize.min, STYLE_RANGES.fontSize.max) + 'px';
  }
  if (typeof s.fontWeight === 'number') {
    out.fontWeight = clamp(s.fontWeight, STYLE_RANGES.fontWeight.min, STYLE_RANGES.fontWeight.max);
  }
  if (typeof s.letterSpacing === 'number') {
    out.letterSpacing = clamp(s.letterSpacing, STYLE_RANGES.letterSpacing.min, STYLE_RANGES.letterSpacing.max) + 'em';
  }
  if (typeof s.lineHeight === 'number') {
    out.lineHeight = clamp(s.lineHeight, STYLE_RANGES.lineHeight.min, STYLE_RANGES.lineHeight.max);
  }
  if (typeof s.textAlign === 'string' && ['left', 'center', 'right'].includes(s.textAlign)) {
    out.textAlign = s.textAlign;
  }
  if (typeof s.opacity === 'number') {
    out.opacity = clamp(s.opacity, STYLE_RANGES.opacity.min, STYLE_RANGES.opacity.max);
  }
  if (typeof s.color === 'string' && s.color.length > 0) {
    out.color = s.color;
  }
  // Font family
  if (typeof s.fontFamily === 'string' && s.fontFamily.length > 0) {
    out.fontFamily = s.fontFamily;
  }
  // Font style (italic)
  if (typeof s.fontStyle === 'string' && ['normal', 'italic'].includes(s.fontStyle)) {
    out.fontStyle = s.fontStyle;
  }
  if (typeof s.borderRadius === 'number') {
    out.borderRadius = clamp(s.borderRadius, STYLE_RANGES.borderRadius.min, STYLE_RANGES.borderRadius.max) + 'px';
  }
  if (typeof s.paddingX === 'number') {
    const px = clamp(s.paddingX, STYLE_RANGES.paddingX.min, STYLE_RANGES.paddingX.max);
    out.paddingLeft = px + 'px';
    out.paddingRight = px + 'px';
  }
  if (typeof s.paddingY === 'number') {
    const py = clamp(s.paddingY, STYLE_RANGES.paddingY.min, STYLE_RANGES.paddingY.max);
    out.paddingTop = py + 'px';
    out.paddingBottom = py + 'px';
  }
  if (typeof s.glassBlur === 'number') {
    const b = clamp(s.glassBlur, STYLE_RANGES.glassBlur.min, STYLE_RANGES.glassBlur.max);
    out.backdropFilter = `blur(${b}px)`;
    out.WebkitBackdropFilter = `blur(${b}px)`;
  }
  if (typeof s.glassAlpha === 'number') {
    const a = clamp(s.glassAlpha, STYLE_RANGES.glassAlpha.min, STYLE_RANGES.glassAlpha.max);
    out.background = `rgba(255, 255, 255, ${a})`;
  }

  // ---- Custom State 2 Glass Properties ----
  // These aren't raw CSS attributes; they are intercepted by GlassCard in ContentCards.jsx.
  if (typeof s.blur === 'number') {
    out.blur = clamp(s.blur, STYLE_RANGES.blur.min, STYLE_RANGES.blur.max);
  }
  if (typeof s.refraction === 'number') {
    out.refraction = clamp(s.refraction, STYLE_RANGES.refraction.min, STYLE_RANGES.refraction.max);
  }
  if (typeof s.depth === 'number') {
    out.depth = clamp(s.depth, STYLE_RANGES.depth.min, STYLE_RANGES.depth.max);
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Get media override for an element.
 * Returns { src, poster, type, ... } with overrides applied on top of fallback.
 * Supports both 'type' and 'kind' fields (kind is an alias for type).
 */
export function getMedia(editId, fallback) {
  const entry = _overrides.elements?.[editId];
  if (!entry || !entry.media) return fallback;
  // Merge media fields — use 'in' to detect explicit null (clears field)
  const result = { ...fallback };
  const m = entry.media;
  if ('src' in m) result.src = m.src;
  if ('poster' in m) result.poster = m.poster;
  // Support both 'type' and 'kind' — kind is an alias for type
  if ('type' in m) result.type = m.type;
  if ('kind' in m) result.type = m.kind;
  // Merge preview settings (deep merge to preserve individual sub-fields)
  if ('preview' in m) {
    if (m.preview && typeof m.preview === 'object') {
      result.preview = { ...(result.preview || {}), ...m.preview };
    } else {
      result.preview = m.preview; // null clears it
    }
  }
  return result;
}

/**
 * Get the crop/focal-point style for a media element.
 * Returns inline style object for the image, or undefined.
 */
export function getCropStyle(editId) {
  const entry = _overrides.elements?.[editId];
  if (!entry || !entry.media) return undefined;
  const m = entry.media;

  // Focal point → object-position (works with object-fit: cover)
  if (m.focalPoint) {
    return {
      objectPosition: `${(m.focalPoint.x * 100).toFixed(1)}% ${(m.focalPoint.y * 100).toFixed(1)}%`,
    };
  }

  // Crop rect → clip-path inset (non-destructive CSS crop)
  if (m.crop) {
    const { x, y, w, h } = m.crop;
    const top = (y * 100).toFixed(1);
    const right = ((1 - x - w) * 100).toFixed(1);
    const bottom = ((1 - y - h) * 100).toFixed(1);
    const left = (x * 100).toFixed(1);
    return {
      clipPath: `inset(${top}% ${right}% ${bottom}% ${left}%)`,
    };
  }

  return undefined;
}

/**
 * Merge base clients with structure overrides (added/deleted/reordered).
 * Used by DetailState for both public site and admin preview.
 * @param {Array} baseClients — original clients from portfolio.json
 * @param {string} variant — 'video' or 'web'
 */
export function mergeClients(baseClients, variant) {
  const structure = _overrides.structure?.[variant];
  if (!structure) return baseClients;

  let clients = [...baseClients];

  // Add new clients
  if (Array.isArray(structure.added)) {
    clients = [...clients, ...structure.added];
  }

  // Remove deleted clients
  if (Array.isArray(structure.deleted) && structure.deleted.length > 0) {
    const deletedSet = new Set(structure.deleted);
    clients = clients.filter(c => !deletedSet.has(c.id));
  }

  // Apply ordering
  if (Array.isArray(structure.order) && structure.order.length > 0) {
    const orderMap = new Map(structure.order.map((id, i) => [id, i]));
    clients.sort((a, b) => {
      const ai = orderMap.has(a.id) ? orderMap.get(a.id) : 9999;
      const bi = orderMap.has(b.id) ? orderMap.get(b.id) : 9999;
      return ai - bi;
    });
  }

  return clients;
}

/**
 * Merge base media items with structure overrides (added/deleted/reordered).
 * Each returned item gets a stable `_id` field:
 *   - Base items: _id = original array index (number)
 *   - Added items: _id = generated string id
 * @param {Array} baseMedia — original media array from portfolio.json
 * @param {string} variant — 'video' or 'web'
 * @param {string} clientId — client identifier
 */
export function mergeMedia(baseMedia, variant, clientId) {
  const cm = _overrides.structure?.[variant]?.clientMedia?.[clientId];

  // No overrides — just stamp _id and normalize each base item
  if (!cm) {
    return baseMedia.map((item, idx) => {
      const n = normalizeMediaItem(item);
      n._id = idx;
      return n;
    });
  }

  // Start with base items (each gets _id = index)
  let items = baseMedia.map((item, idx) => ({ ...item, _id: idx }));

  // Add new items
  if (Array.isArray(cm.added)) {
    items = [...items, ...cm.added.map((a) => ({ ...a, _id: a.id }))];
  }

  // Remove deleted items (compare as strings for mixed-type safety)
  if (Array.isArray(cm.deleted) && cm.deleted.length > 0) {
    const deletedSet = new Set(cm.deleted.map(String));
    items = items.filter((item) => !deletedSet.has(String(item._id)));
  }

  // Apply ordering
  if (Array.isArray(cm.order) && cm.order.length > 0) {
    const orderMap = new Map(cm.order.map((id, i) => [String(id), i]));
    items.sort((a, b) => {
      const ai = orderMap.has(String(a._id)) ? orderMap.get(String(a._id)) : 9999;
      const bi = orderMap.has(String(b._id)) ? orderMap.get(String(b._id)) : 9999;
      return ai - bi;
    });
  }

  // Normalize every item to guarantee consistent shape (type, src, poster)
  return items.map((item) => {
    const n = normalizeMediaItem(item);
    n._id = item._id; // preserve merge-assigned stable _id
    return n;
  });
}

/**
 * Normalize a media item to ensure all required fields are present.
 * Used defensively on render and on creation to prevent blank/inconsistent cards.
 * @param {object} raw — raw media item (may have missing fields)
 * @returns {object} — media item with all required fields set
 */
export function normalizeMediaItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return { id: _quickId(), type: 'image', src: null, poster: null };
  }
  const item = { ...raw };
  // Ensure stable id
  if (item.id == null && item._id == null) {
    item.id = _quickId();
  }
  // Ensure type (infer from src extension if missing)
  if (!item.type && !item.kind) {
    const s = item.src || '';
    item.type = /\.(webm|mp4|mov|avi|mkv|m4v|ogv)(\?.*)?$/i.test(s) ? 'video' : 'image';
  }
  // Ensure src and poster are string|null (not undefined)
  if (item.src === undefined) item.src = null;
  if (item.poster === undefined) item.poster = null;
  return item;
}
function _quickId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return 'gen_' + crypto.randomUUID().slice(0, 8);
  return 'gen_' + Math.random().toString(36).slice(2, 10);
}

/** Check if an ID was generated by the admin (gen_* prefix). */
function _isGenId(id) {
  return typeof id === 'string' && id.startsWith('gen_');
}

/** Resolve effective src for a media item, checking element overrides. */
function _resolvedSrc(m, variant, clientId, overrides) {
  if (m.src) return m.src;
  if (!overrides || !overrides.elements) return null;
  const mid = m._id != null ? m._id : m.id;
  if (mid == null) return null;
  const editId = `state3.${variant}.client.${clientId}.media.${mid}`;
  return overrides.elements[editId]?.media?.src || null;
}

/**
 * Validate portfolio data before publish.
 * Returns array of error strings (empty = valid).
 */
export function validatePortfolio(portfolio, overrides) {
  const errors = [];
  if (!portfolio) return ['Portfolio not loaded'];
  for (const variant of ['video', 'web']) {
    const cat = portfolio[variant];
    if (!cat || !cat.clients) continue;
    const clients = mergeClients(cat.clients, variant);
    for (const client of clients) {
      const media = mergeMedia(client.media || [], variant, client.id);
      // Check for duplicate media IDs
      const ids = new Set();
      for (const m of media) {
        const mid = String(m._id);
        if (ids.has(mid)) {
          errors.push(`[${variant}/${client.name}] Duplicate media ID: ${mid}`);
        }
        ids.add(mid);
      }
      // Check for empty src — resolve element overrides first
      for (const m of media) {
        const effectiveSrc = _resolvedSrc(m, variant, client.id, overrides);
        if (!effectiveSrc) {
          const mid = m._id != null ? m._id : '?';
          errors.push(`[${variant}/${client.name}] Media "${mid}" has no source — upload a file or remove it`);
        }
      }
      // Check videos have poster
      for (const m of media) {
        const kind = m.type || m.kind || 'image';
        if (kind === 'video' && !m.poster && m.src) {
          const mid = m._id != null ? m._id : '?';
          errors.push(`[${variant}/${client.name}] Video "${mid}" has no poster image`);
        }
      }
    }
  }
  return errors;
}

/**
 * Auto-remove empty gen_* placeholders before publish.
 * Removes media with no src (neither in structure nor element overrides)
 * and empty "New Client" entries with no content.
 * Returns { cleaned, removedCount }.
 */
export function stripEmptyPlaceholders(overrides, portfolio) {
  const next = JSON.parse(JSON.stringify(overrides));
  let removedCount = 0;

  for (const variant of ['video', 'web']) {
    const cm = next.structure?.[variant]?.clientMedia;
    if (!cm) continue;
    for (const clientId of Object.keys(cm)) {
      const entry = cm[clientId];
      if (!Array.isArray(entry.added)) continue;
      const before = entry.added.length;
      entry.added = entry.added.filter((a) => {
        if (a.src) return true;
        const mid = a.id;
        const editId = `state3.${variant}.client.${clientId}.media.${mid}`;
        if (next.elements[editId]?.media?.src) return true;
        // Empty placeholder — remove element overrides and order entry
        delete next.elements[editId];
        if (Array.isArray(entry.order)) {
          entry.order = entry.order.filter((id) => String(id) !== String(mid));
        }
        return false;
      });
      removedCount += before - entry.added.length;
    }
  }

  // Also strip empty "New Client" entries
  for (const variant of ['video', 'web']) {
    const sv = next.structure?.[variant];
    if (!sv || !Array.isArray(sv.added)) continue;
    const before = sv.added.length;
    sv.added = sv.added.filter((c) => {
      if (!_isGenId(c.id)) return true;
      if (c.name && c.name !== 'New Client') return true;
      const clientMedia = next.structure?.[variant]?.clientMedia?.[c.id];
      const hasMedia = clientMedia?.added?.some((a) => {
        if (a.src) return true;
        const editId = `state3.${variant}.client.${c.id}.media.${a.id}`;
        return !!next.elements[editId]?.media?.src;
      });
      if (hasMedia) return true;
      // Empty client — clean up related overrides
      const prefix = `state3.${variant}.client.${c.id}`;
      Object.keys(next.elements).forEach((key) => {
        if (key.startsWith(prefix)) delete next.elements[key];
      });
      if (sv.order) sv.order = sv.order.filter((id) => id !== c.id);
      if (next.structure[variant].clientMedia?.[c.id]) {
        delete next.structure[variant].clientMedia[c.id];
      }
      return false;
    });
    removedCount += before - sv.added.length;
  }

  return { cleaned: next, removedCount };
}

/** Export STYLE_RANGES for use in the admin settings panel. */
export { STYLE_RANGES };
